import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { parseUtc } from '../lib/calendarDate';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { BetaBanner } from '../components/BetaBanner';
import HomeChecklist from '../components/HomeChecklist';
import ShareModal from '../components/ShareModal';
import {
  Sparkles, ArrowRight, GraduationCap, Megaphone, Video, Plus,
  CalendarDays, TrendingUp, Coins, UsersRound, MessageSquare,
  Compass, Package, CreditCard,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/* ──────────────────────────────────────────────────────────────────────────
   Zone 1 — Greeting compact ("Bonjour {firstName}" + date) — inchangé
   ────────────────────────────────────────────────────────────────────────── */
const ZoneGreeting = () => {
  const [firstName, setFirstName] = useState('');
  useEffect(() => {
    axios.get(`${API}/home/greeting`, { withCredentials: true })
      .then(({ data }) => setFirstName(data.first_name || ''))
      .catch(() => setFirstName(''));
  }, []);
  const today = new Date().toLocaleDateString('fr-FR', {
    weekday: 'long', day: 'numeric', month: 'long',
  });
  return (
    <div data-testid="zone-greeting">
      <h1 className="text-2xl font-medium text-gray-900 dark:text-white">
        {firstName ? `Bonjour ${firstName}` : 'Bonjour'}
      </h1>
      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 capitalize">{today}</p>
    </div>
  );
};

/* ──────────────────────────────────────────────────────────────────────────
   Zone 2 — Intro contextuelle (Brief Jun 14 — Home v3 wording simplifié)
   ────────────────────────────────────────────────────────────────────────── */
const ZoneIntro = () => (
  <section
    data-testid="zone-intro"
    className="px-5 py-3.5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
    style={{
      background: 'var(--color-background-primary)',
      border: '0.5px solid var(--color-border-tertiary)',
      borderRadius: 'var(--border-radius-lg)',
    }}
  >
    <div className="min-w-0">
      <p className="text-[14px] font-medium" style={{ color: 'var(--color-text-primary)' }}>
        OpenSapiens — apprends des meilleurs, transmets ton savoir.
      </p>
      <p className="text-[12px] mt-0.5" style={{ color: 'var(--color-text-secondary)' }}>
        Tout depuis un seul compte. Voici ce qui t&apos;attend aujourd&apos;hui.
      </p>
    </div>
    <span
      data-testid="intro-beta-badge"
      className="self-start sm:self-center text-[11px] font-semibold px-2.5 py-1 rounded-full whitespace-nowrap"
      style={{ backgroundColor: '#EEEDFE', color: '#3C3489' }}
    >
      Beta · Gratuit
    </span>
  </section>
);

/* ──────────────────────────────────────────────────────────────────────────
   Zone 3 — Deux colonnes d'action Apprendre (teal) / Transmettre (violet)
   Brief Jun 14 — Home v3 : headers colorés pleine largeur, items 3 niveaux,
   footer 2 CTAs (outline + filled). Remplace les colonnes plates de Jun 11.
   ────────────────────────────────────────────────────────────────────────── */
const LEARN_ACCENT = '#1D9E75';
const LEARN_ICON_BG = '#085041';
const LEARN_ICON_FG = '#5DCAA5';
const LEARN_BTN_FILL_TEXT = '#E1F5EE';
const TRANSMIT_ACCENT = '#7F77DD';
const TRANSMIT_ICON_BG = '#3C3489';
const TRANSMIT_ICON_FG = '#AFA9EC';
const TRANSMIT_BTN_FILL_TEXT = '#EEEDFE';

/* Header pleine largeur (fond accent, icône 32px, titre + sous-titre blancs). */
const ColumnHeader = ({ bg, iconBg, iconFg, icon: Icon, title, subtitle, testidTitle }) => (
  <div
    className="flex items-center gap-3 px-4 py-3"
    style={{ background: bg }}
  >
    <div
      className="h-8 w-8 rounded-lg flex items-center justify-center shrink-0"
      style={{ background: iconBg, color: iconFg }}
    >
      <Icon className="h-4 w-4" />
    </div>
    <div className="min-w-0">
      <p
        className="text-[14px] font-medium leading-tight"
        style={{ color: '#FFFFFF' }}
        data-testid={testidTitle}
      >
        {title}
      </p>
      <p
        className="text-[11px] leading-tight mt-0.5"
        style={{ color: 'rgba(255,255,255,0.75)' }}
      >
        {subtitle}
      </p>
    </div>
  </div>
);

/* Item 3 niveaux : icône colorée + main + méta + CTA optionnel. */
const ColumnItem = ({ accent, icon: Icon, main, meta, cta, testid }) => (
  <div
    data-testid={testid}
    className="px-2.5 py-2 flex items-center gap-2.5"
    style={{
      background: 'var(--color-background-secondary)',
      borderRadius: 'var(--border-radius-md)',
    }}
  >
    <Icon className="h-4 w-4 shrink-0" style={{ color: accent }} />
    <div className="flex-1 min-w-0">
      <p
        className="text-[12px] font-medium leading-tight truncate"
        style={{ color: 'var(--color-text-primary)' }}
      >
        {main}
      </p>
      {meta && (
        <p
          className="text-[10px] leading-tight mt-0.5 truncate"
          style={{ color: 'var(--color-text-secondary)' }}
        >
          {meta}
        </p>
      )}
    </div>
    {cta}
  </div>
);

/* CTA pill (variant: filled | outline). */
const FooterCta = ({ variant, accent, fillTextColor, onClick, href, children, testid }) => {
  const baseCls = 'flex-1 text-[12px] font-semibold rounded-lg px-3 py-2 inline-flex items-center justify-center gap-1 hover:opacity-90 transition-opacity whitespace-nowrap';
  const style = variant === 'filled'
    ? { background: accent, color: fillTextColor, border: `1px solid ${accent}` }
    : { background: 'transparent', color: accent, border: `1px solid ${accent}` };
  if (href) {
    return (
      <a href={href} className={baseCls} style={style} data-testid={testid}>{children}</a>
    );
  }
  return (
    <button type="button" onClick={onClick} className={baseCls} style={style} data-testid={testid}>
      {children}
    </button>
  );
};

/* CTA inline d'item (compact). */
const ItemCta = ({ accent, fillTextColor, onClick, href, children, testid, disabled }) => {
  const cls = 'text-[11px] font-semibold rounded-md px-2.5 py-1 inline-flex items-center gap-1 hover:opacity-90 transition-opacity whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed';
  const style = { background: accent, color: fillTextColor };
  if (href) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" className={cls} style={style} data-testid={testid}>
        {children}
      </a>
    );
  }
  return (
    <button type="button" onClick={onClick} disabled={disabled} className={cls} style={style} data-testid={testid}>
      {children}
    </button>
  );
};

/* Shell : header coloré pleine largeur + items + footer 2 CTAs. */
const ColumnShell = ({ testid, header, children, footer }) => (
  <div
    data-testid={testid}
    className="flex flex-col overflow-hidden"
    style={{
      background: 'var(--color-background-primary)',
      border: '0.5px solid var(--color-border-tertiary)',
      borderRadius: 'var(--border-radius-lg)',
    }}
  >
    {header}
    <div className="flex-1 px-3 py-3 space-y-2">{children}</div>
    <div className="px-3 pb-3 flex gap-2">{footer}</div>
  </div>
);

const fmtEventDate = (iso) => {
  if (!iso) return '';
  const d = parseUtc(iso);
  if (!d || isNaN(d)) return '';
  return `${d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })} · ${d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}`;
};

const LearnColumn = ({ data, navigate }) => {
  const booked = data?.next_booked;
  const trending = data?.trending;
  return (
    <ColumnShell
      testid="home-column-learn"
      header={
        <ColumnHeader
          bg={LEARN_ACCENT}
          iconBg={LEARN_ICON_BG}
          iconFg={LEARN_ICON_FG}
          icon={GraduationCap}
          title="Apprendre"
          subtitle="Trouve ton prochain expert"
          testidTitle="learn-header-title"
        />
      }
      footer={
        <>
          <FooterCta
            variant="outline"
            accent={LEARN_ACCENT}
            onClick={() => navigate('/app/discover')}
            testid="learn-footer-explore"
          >
            Explorer
          </FooterCta>
          <FooterCta
            variant="filled"
            accent={LEARN_ACCENT}
            fillTextColor={LEARN_BTN_FILL_TEXT}
            onClick={() => navigate('/app/discover')}
            testid="learn-footer-book"
          >
            Réserver
          </FooterCta>
        </>
      }
    >
      {/* Item 1 — Experts tendance */}
      {trending ? (
        <ColumnItem
          accent={LEARN_ACCENT}
          icon={TrendingUp}
          main={<><strong>{trending.experts_count}</strong> expert{trending.experts_count > 1 ? 's' : ''} en {trending.label}</>}
          meta="Tendance cette semaine"
          testid="learn-trending"
          cta={
            <ItemCta
              accent={LEARN_ACCENT}
              fillTextColor={LEARN_BTN_FILL_TEXT}
              onClick={() => navigate(`/app/discover?q=${encodeURIComponent(trending.label)}`)}
              testid="learn-trending-cta"
            >
              Voir
            </ItemCta>
          }
        />
      ) : (
        <ColumnItem
          accent={LEARN_ACCENT}
          icon={Compass}
          main="Découvre des experts disponibles"
          meta="Tendance cette semaine"
          testid="learn-trending-empty"
          cta={
            <ItemCta
              accent={LEARN_ACCENT}
              fillTextColor={LEARN_BTN_FILL_TEXT}
              onClick={() => navigate('/app/discover')}
              testid="learn-trending-empty-cta"
            >
              Voir
            </ItemCta>
          }
        />
      )}

      {/* Item 2 — Prochaine session réservée */}
      {booked ? (
        <ColumnItem
          accent={LEARN_ACCENT}
          icon={Video}
          main={booked.title + (booked.expert?.display_name ? ` · ${booked.expert.display_name}` : '')}
          meta={fmtEventDate(booked.start_time)}
          testid="learn-next-booked"
          cta={
            booked.visio_link ? (
              <ItemCta
                accent={LEARN_ACCENT}
                fillTextColor={LEARN_BTN_FILL_TEXT}
                href={booked.visio_link}
                testid="learn-join-cta"
              >
                Rejoindre
              </ItemCta>
            ) : (
              <ItemCta
                accent={LEARN_ACCENT}
                fillTextColor={LEARN_BTN_FILL_TEXT}
                onClick={() => navigate('/app/sessions')}
                testid="learn-view-booking-cta"
              >
                Voir
              </ItemCta>
            )
          }
        />
      ) : (
        <ColumnItem
          accent={LEARN_ACCENT}
          icon={CalendarDays}
          main="Aucune session à venir"
          meta="Réserve ta première session"
          testid="learn-next-empty"
        />
      )}

      {/* Item 3 — MaIA */}
      <ColumnItem
        accent={LEARN_ACCENT}
        icon={Sparkles}
        main="Demander à MaIA"
        meta="Trouve l'expert qu'il te faut"
        testid="learn-maia"
        cta={
          <ItemCta
            accent={LEARN_ACCENT}
            fillTextColor={LEARN_BTN_FILL_TEXT}
            onClick={() => navigate('/app/assistant')}
            testid="learn-maia-cta"
          >
            Go
          </ItemCta>
        }
      />
    </ColumnShell>
  );
};

const TransmitColumn = ({ data, navigate, currentUserId }) => {
  const pendingCount = data?.pending_count || 0;
  const productsCount = data?.products_count || 0;
  const downloads = data?.products_downloads || 0;
  const followersCount = data?.followers_count || 0;
  const upcomingPublic = data?.upcoming_public_event;
  // Brief Jul 3 2026 — Stripe CTA en tête de colonne Transmettre tant que
  // l'expert n'a pas activé les paiements. Utilise l'endpoint existant
  // POST /api/stripe/connect/onboard pour ouvrir l'onboarding hosted.
  const stripeConnected = !!data?.stripe_connected;
  const [stripeBusy, setStripeBusy] = useState(false);
  // Brief Jul 10 2026 — résumé Prospection (stats 7 jours, deep-link Mes
  // sessions ?tab=prospection). Réutilise l'endpoint existant du dashboard.
  const [prosp, setProsp] = useState(null);
  const [shareProfileOpen, setShareProfileOpen] = useState(false);
  useEffect(() => {
    axios.get(`${API}/prospection/stats?days=7`, { withCredentials: true })
      .then(({ data: r }) => setProsp(r))
      .catch(() => {});
  }, []);
  const prospVisits = prosp?.visits_total || 0;
  const prospBookings = prosp?.bookings_attributed || 0;
  const prospEmpty = prospVisits === 0 && prospBookings === 0;
  const handleConnectStripe = async () => {
    if (stripeBusy) return;
    setStripeBusy(true);
    try {
      const { data: r } = await axios.post(`${API}/stripe/connect/onboard`, {}, { withCredentials: true });
      if (r?.url) {
        window.location.href = r.url;
        return;
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Impossible de démarrer Stripe.');
      setStripeBusy(false);
    }
  };
  return (
    <ColumnShell
      testid="home-column-transmit"
      header={
        <ColumnHeader
          bg={TRANSMIT_ACCENT}
          iconBg={TRANSMIT_ICON_BG}
          iconFg={TRANSMIT_ICON_FG}
          icon={Megaphone}
          title="Transmettre"
          subtitle="Partage ton expertise"
          testidTitle="transmit-header-title"
        />
      }
      footer={
        <>
          <FooterCta
            variant="outline"
            accent={TRANSMIT_ACCENT}
            onClick={() => navigate('/app/sessions')}
            testid="transmit-footer-new-session"
          >
            Nouvelle session
          </FooterCta>
          <FooterCta
            variant="filled"
            accent={TRANSMIT_ACCENT}
            fillTextColor={TRANSMIT_BTN_FILL_TEXT}
            onClick={() => currentUserId && navigate(`/app/shop/${currentUserId}`)}
            testid="transmit-footer-add-content"
          >
            Ajouter contenu
          </FooterCta>
        </>
      }
    >
      {/* Brief Jul 3 2026 — Item Stripe CTA (colonne Transmettre, 1re position)
          visible tant que l'expert n'a pas activé les paiements. Bordure gauche
          ambre pour se distinguer visuellement des autres items violets. */}
      {!stripeConnected && (
        <ColumnItem
          accent="#BA7517"
          icon={CreditCard}
          main={<><strong>Active les paiements</strong></>}
          meta="Reçois de l'argent pour tes sessions et produits"
          testid="transmit-stripe-cta"
          cta={
            <ItemCta
              accent="#BA7517"
              fillTextColor="#FFFFFF"
              onClick={handleConnectStripe}
              disabled={stripeBusy}
              testid="transmit-stripe-connect-btn"
            >
              {stripeBusy ? '…' : 'Connecter Stripe'}
            </ItemCta>
          }
        />
      )}

      {/* Item 1 — Demandes en attente */}
      {pendingCount > 0 ? (
        <ColumnItem
          accent={TRANSMIT_ACCENT}
          icon={CalendarDays}
          main={<><strong>{pendingCount}</strong> demande{pendingCount > 1 ? 's' : ''} en attente</>}
          meta="À accepter"
          testid="transmit-pending"
          cta={
            <ItemCta
              accent={TRANSMIT_ACCENT}
              fillTextColor={TRANSMIT_BTN_FILL_TEXT}
              onClick={() => navigate('/app/sessions')}
              testid="transmit-pending-cta"
            >
              Voir
            </ItemCta>
          }
        />
      ) : (
        <ColumnItem
          accent={TRANSMIT_ACCENT}
          icon={CalendarDays}
          main="Aucune demande en attente"
          meta="Crée une session pour commencer"
          testid="transmit-pending-empty"
          cta={
            <ItemCta
              accent={TRANSMIT_ACCENT}
              fillTextColor={TRANSMIT_BTN_FILL_TEXT}
              onClick={() => navigate('/app/sessions')}
              testid="transmit-pending-empty-cta"
            >
              <Plus className="h-3 w-3" /> Créer
            </ItemCta>
          }
        />
      )}

      {/* Brief Jul 10 2026 — Item Prospection (stats 7 jours) entre demandes
          en attente et boutique. État vide → CTA Partager (ShareModal profil). */}
      <ColumnItem
        accent={TRANSMIT_ACCENT}
        icon={TrendingUp}
        main={prospEmpty
          ? 'Partage tes sessions pour attirer des apprenants'
          : <><strong>{prospVisits}</strong> visite{prospVisits > 1 ? 's' : ''} cette semaine · <strong>{prospBookings}</strong> réservation{prospBookings > 1 ? 's' : ''}</>}
        meta="Prospection"
        testid="transmit-prospection"
        cta={
          <ItemCta
            accent={TRANSMIT_ACCENT}
            fillTextColor={TRANSMIT_BTN_FILL_TEXT}
            onClick={prospEmpty
              ? () => setShareProfileOpen(true)
              : () => navigate('/app/sessions?tab=prospection')}
            testid={prospEmpty ? 'transmit-prospection-share-cta' : 'transmit-prospection-cta'}
          >
            {prospEmpty ? 'Partager' : 'Voir'}
          </ItemCta>
        }
      />
      <ShareModal
        isOpen={shareProfileOpen}
        onClose={() => setShareProfileOpen(false)}
        shareType="profile"
        shareData={{ id: currentUserId, title: 'Mon profil OpenSapiens', description: 'Découvre mon profil et mes sessions sur OpenSapiens' }}
      />

      {/* Item 2 — Boutique */}
      <ColumnItem
        accent={TRANSMIT_ACCENT}
        icon={Package}
        main={<><strong>{productsCount}</strong> produit{productsCount > 1 ? 's' : ''} · {downloads} téléchargement{downloads > 1 ? 's' : ''}</>}
        meta="Partage ton contenu"
        testid="transmit-products"
        cta={
          <ItemCta
            accent={TRANSMIT_ACCENT}
            fillTextColor={TRANSMIT_BTN_FILL_TEXT}
            onClick={() => currentUserId && navigate(`/app/shop/${currentUserId}`)}
            testid="transmit-products-cta"
          >
            Voir
          </ItemCta>
        }
      />

      {/* Item 3 — Abonnés (Brief Jun 14) */}
      <ColumnItem
        accent={TRANSMIT_ACCENT}
        icon={UsersRound}
        main={<><strong>{followersCount}</strong> abonné{followersCount > 1 ? 's' : ''}</>}
        meta="Développe ta communauté"
        testid="transmit-followers"
      />

      {/* Item 4 dynamique — Inscrits à un event public (Brief Vague 2 §4) */}
      {upcomingPublic && (
        <ColumnItem
          accent={TRANSMIT_ACCENT}
          icon={Megaphone}
          main={<><strong>{upcomingPublic.participants_count}</strong> inscrit{upcomingPublic.participants_count > 1 ? 's' : ''} à « {upcomingPublic.title} »</>}
          meta="Prochain événement public"
          testid="transmit-public-participants"
          cta={
            <ItemCta
              accent={TRANSMIT_ACCENT}
              fillTextColor={TRANSMIT_BTN_FILL_TEXT}
              onClick={() => navigate('/app/sessions')}
              testid="transmit-public-participants-cta"
            >
              Voir
            </ItemCta>
          }
        />
      )}
    </ColumnShell>
  );
};

const ZoneDualColumns = ({ currentUserId }) => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const reload = useCallback(() => {
    axios.get(`${API}/home/dual-columns`, { withCredentials: true })
      .then(({ data }) => setData(data))
      .catch(() => setData({ learn: {}, transmit: {} }));
  }, []);
  useEffect(() => {
    reload();
    // Brief Jul 3 2026 — One-shot toast quand on revient de l'onboarding
    // Stripe hosted (?stripe=success dans l'URL). Guardé par sessionStorage
    // pour éviter le toast double en strict-mode.
    // Brief Jul 6 2026 §4 — Re-fetch dual-columns après ?stripe=success pour
    // que l'item "Active les paiements" disparaisse immédiatement dès que
    // stripe_charges_enabled=true (webhook account.updated a été reçu).
    // On tente plusieurs re-fetches espacés pour laisser le webhook arriver.
    const params = new URLSearchParams(window.location.search);
    if (params.get('stripe') === 'success') {
      if (!sessionStorage.getItem('stripe_success_shown')) {
        sessionStorage.setItem('stripe_success_shown', '1');
        toast.success('✅ Stripe connecté — tes sessions payantes sont maintenant actives !', { duration: 6000 });
      }
      const url = new URL(window.location.href);
      url.searchParams.delete('stripe');
      window.history.replaceState({}, '', url.toString());
      // Re-fetches échelonnés (2s, 6s, 12s) pour attraper le webhook.
      // Chaque re-fetch hit d'abord /stripe/connect/status qui force-sync
      // charges_enabled depuis Stripe API vers la DB — utile si le webhook
      // account.updated tarde ou est manqué.
      const syncAndReload = async () => {
        try { await axios.get(`${API}/stripe/connect/status`, { withCredentials: true }); } catch { /* ignore */ }
        reload();
      };
      const t1 = setTimeout(syncAndReload, 2000);
      const t2 = setTimeout(syncAndReload, 6000);
      const t3 = setTimeout(syncAndReload, 12000);
      return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
    }
  }, [reload]);
  if (!data) {
    return (
      <section className="grid grid-cols-1 md:grid-cols-2" style={{ gap: 10 }} data-testid="zone-dual-loading">
        <div className="rounded-xl bg-gray-100 dark:bg-gray-800/40 h-[170px] animate-pulse" />
        <div className="rounded-xl bg-gray-100 dark:bg-gray-800/40 h-[170px] animate-pulse" />
      </section>
    );
  }
  return (
    <section className="grid grid-cols-1 md:grid-cols-2" style={{ gap: 10 }} data-testid="zone-dual-columns">
      <LearnColumn data={data.learn} navigate={navigate} />
      <TransmitColumn data={data.transmit} navigate={navigate} currentUserId={currentUserId} />
    </section>
  );
};

/* ──────────────────────────────────────────────────────────────────────────
   Zone 4 — Stats analytics inline (current month) — inchangé
   ────────────────────────────────────────────────────────────────────────── */
const ZoneStats = () => {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    axios.get(`${API}/home/dashboard-stats`, { withCredentials: true })
      .then(({ data }) => setStats(data))
      .catch(() => setStats({ sessions_done: 0, revenue_chf: 0, followers_total: 0 }));
  }, []);
  if (!stats) {
    return (
      <section className="grid grid-cols-3 gap-2.5" data-testid="zone-stats-loading">
        {[0, 1, 2].map((i) => (
          <div key={i} className="rounded-lg bg-gray-100 dark:bg-gray-800/40 h-[68px] animate-pulse" />
        ))}
      </section>
    );
  }
  const tiles = [
    { key: 'sessions', label: 'Sessions réalisées', value: stats.sessions_done, Icon: CalendarDays, testid: 'metric-sessions-done' },
    { key: 'revenue', label: 'Revenus', value: `${Math.round(stats.revenue_chf || 0)} CHF`, Icon: Coins, testid: 'metric-revenue' },
    { key: 'followers', label: 'Abonnés', value: stats.followers_total, Icon: UsersRound, testid: 'metric-followers' },
  ];
  return (
    <section className="grid grid-cols-3 gap-2.5" data-testid="zone-stats">
      {tiles.map(({ key, label, value, Icon, testid }) => (
        <div
          key={key}
          className="bg-gray-50 dark:bg-gray-800/40 rounded-lg px-3 py-2.5 flex items-center gap-2.5"
          data-testid={testid}
        >
          <div className="h-7 w-7 rounded-md bg-white dark:bg-gray-900/60 flex items-center justify-center text-purple-500 shrink-0">
            <Icon className="h-3.5 w-3.5" />
          </div>
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400 truncate">{label}</p>
            <p className="text-[15px] font-semibold text-gray-900 dark:text-white leading-tight">{value}</p>
          </div>
        </div>
      ))}
    </section>
  );
};

/* ──────────────────────────────────────────────────────────────────────────
   Zone 5 — Tendances + Communauté (côte à côte) — inchangé
   ────────────────────────────────────────────────────────────────────────── */
const TrendsBox = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  useEffect(() => {
    axios.get(`${API}/home/weekly`, { withCredentials: true })
      .then(({ data }) => setData(data)).catch(() => setData(null));
  }, []);
  if (!data) return <div className="rounded-xl bg-gray-100 dark:bg-gray-800/40 h-[160px] animate-pulse" />;
  const items = [];
  if (data.member_spotlight) {
    items.push({
      key: 'member',
      onClick: () => data.member_spotlight.user_id && navigate(`/app/profile/${data.member_spotlight.user_id}`),
      content: <>Nouveau membre&nbsp;: <strong>{data.member_spotlight.display_name}</strong>{data.member_description ? `, ${data.member_description}` : ''}</>,
    });
  }
  if (data.conversation_pick) {
    const auth = data.conversation_pick.author?.display_name || 'Anonyme';
    const summary = data.conversation_summary || (data.conversation_pick.content || '').slice(0, 60);
    items.push({
      key: 'conv',
      onClick: () => navigate('/app/feed'),
      content: <>Conversation&nbsp;: «&nbsp;{summary}&nbsp;» — <strong>{auth}</strong></>,
    });
  }
  if (data.theme) {
    items.push({
      key: 'theme',
      onClick: () => navigate(`/app/discover?q=${encodeURIComponent(data.theme.label)}`),
      content: <>Thème&nbsp;: <strong>{data.theme.label}</strong>{data.theme.experts_count ? `, ${data.theme.experts_count} experts` : ''}</>,
    });
  }
  return (
    <div
      className="rounded-xl bg-white dark:bg-gray-800/60 border border-gray-200/70 dark:border-gray-700/60 p-3.5 flex flex-col"
      style={{ borderWidth: '0.5px' }}
      data-testid="zone-trends"
    >
      <p className="text-[10px] uppercase tracking-wider font-semibold text-gray-400 dark:text-gray-500 mb-2 flex items-center gap-1">
        <TrendingUp className="h-3 w-3" /> Tendances
      </p>
      {items.length === 0 ? (
        <p className="text-[11px] text-gray-400 dark:text-gray-500">Aucune tendance cette semaine.</p>
      ) : (
        <ul className="space-y-1.5 flex-1">
          {items.slice(0, 3).map(it => (
            <li key={it.key}>
              <button
                type="button"
                onClick={it.onClick}
                className="text-left text-[12px] text-gray-700 dark:text-gray-200 hover:text-purple-600 transition-colors line-clamp-1"
                data-testid={`trend-item-${it.key}`}
              >
                · {it.content}
              </button>
            </li>
          ))}
        </ul>
      )}
      <button
        type="button"
        onClick={() => navigate('/app/discover')}
        className="text-[11px] font-semibold text-purple-700 dark:text-purple-300 hover:underline mt-2 inline-flex items-center gap-0.5"
        data-testid="trends-see-all"
      >
        Voir tout <ArrowRight className="h-3 w-3" />
      </button>
    </div>
  );
};

const CommunityBox = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  useEffect(() => {
    axios.get(`${API}/home/community-feed`, { withCredentials: true })
      .then(({ data }) => setData(data)).catch(() => setData(null));
  }, []);
  if (!data) return <div className="rounded-xl bg-gray-100 dark:bg-gray-800/40 h-[160px] animate-pulse" />;
  return (
    <div
      className="rounded-xl bg-white dark:bg-gray-800/60 border border-gray-200/70 dark:border-gray-700/60 p-3.5 flex flex-col"
      style={{ borderWidth: '0.5px' }}
      data-testid="zone-community"
    >
      <p className="text-[10px] uppercase tracking-wider font-semibold text-gray-400 dark:text-gray-500 mb-2 flex items-center gap-1">
        <MessageSquare className="h-3 w-3" /> Communauté
      </p>
      {!data.items || data.items.length === 0 ? (
        <p className="text-[11px] text-gray-400 dark:text-gray-500">Rien de neuf côté communauté.</p>
      ) : (
        <ul className="space-y-1.5 flex-1">
          {data.items.slice(0, 3).map((it) => (
            <li key={it.post_id}>
              <button
                type="button"
                onClick={() => navigate('/app/feed')}
                className="text-left text-[12px] text-gray-700 dark:text-gray-200 hover:text-purple-600 transition-colors line-clamp-1 w-full"
                data-testid={`community-snippet-${it.post_id}`}
              >
                · <strong>{it.author?.display_name || 'Anonyme'}</strong>&nbsp;: «&nbsp;{it.snippet}&nbsp;»
              </button>
            </li>
          ))}
        </ul>
      )}
      <button
        type="button"
        onClick={() => navigate('/app/feed')}
        className="text-[11px] font-semibold text-purple-700 dark:text-purple-300 hover:underline mt-2 inline-flex items-center gap-0.5"
        data-testid="community-see-all"
      >
        Voir tout <ArrowRight className="h-3 w-3" />
      </button>
    </div>
  );
};

const ZoneTrendsCommunity = () => (
  <section className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="zone-trends-community">
    <TrendsBox />
    <CommunityBox />
  </section>
);

/* ──────────────────────────────────────────────────────────────────────────
   Zone 6 — MaIA entry banner (renommée MaIA — Brief Jun 11)
   ────────────────────────────────────────────────────────────────────────── */
const ZoneMayaEntry = () => {
  const navigate = useNavigate();
  return (
    <section
      data-testid="zone-maya-entry"
      className="bg-white dark:bg-gray-800/60 border border-gray-200/70 dark:border-gray-700/60 rounded-xl px-4 py-3 flex items-center gap-3"
      style={{ borderWidth: '0.5px' }}
    >
      <div
        className="h-8 w-8 rounded-full flex items-center justify-center shrink-0"
        style={{ backgroundColor: '#EEEDFE' }}
      >
        <Sparkles className="h-4 w-4" style={{ color: '#534AB7' }} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[12px] font-medium text-gray-900 dark:text-white">MaIA — ton assistante OpenSapiens</p>
        <p className="text-[11px] text-gray-500 dark:text-gray-400 truncate">
          Trouver un expert, créer une session, mettre en valeur ton profil…
        </p>
      </div>
      <button
        type="button"
        onClick={() => navigate('/app/assistant')}
        data-testid="maya-entry-cta"
        className="text-[11px] font-semibold rounded-lg px-3 py-1.5 inline-flex items-center gap-1 transition-colors hover:bg-purple-50 dark:hover:bg-purple-900/30 whitespace-nowrap"
        style={{ borderWidth: '1px', borderStyle: 'solid', borderColor: '#534AB7', color: '#534AB7' }}
      >
        Poser une question <ArrowRight className="h-3 w-3" />
      </button>
    </section>
  );
};

/* ──────────────────────────────────────────────────────────────────────────
   Main page — Brief Jun 11 : Greeting → Intro → Checklist → Deux colonnes →
   Stats → Tendances/Communauté → MaIA
   ────────────────────────────────────────────────────────────────────────── */
const AppHome = () => {
  const [currentUserId, setCurrentUserId] = useState(null);
  const fetchUser = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setCurrentUserId(data.user_id);
    } catch { /* ignore */ }
  }, []);
  useEffect(() => { fetchUser(); }, [fetchUser]);

  return (
    <div className="space-y-5" data-testid="app-home-page">
      <BetaBanner variant="light" />
      <ZoneGreeting />
      <ZoneIntro />
      {/* Sprint 1.0c — "Premiers pas" checklist (auto-hidden when ≥70% or dismissed) */}
      <HomeChecklist />
      <ZoneDualColumns currentUserId={currentUserId} />
      <ZoneStats />
      <ZoneTrendsCommunity />
      <ZoneMayaEntry />
    </div>
  );
};

export default AppHome;
